import React, { useEffect, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import {
    deleteBranch,
    editBranch,
    getInstitutionBranches,
    addBranch,
} from "../../services/branch";
import { getBranchWorkingDays } from "../../services/branch-workingday";

import Table from "../../components/Table/Table";
import ConfirmationModal from "../../components/Modals/ConfirmationModal/ConfirmationModal";
import BranchDetailsModal from "../../components/Modals/BranchDetailsModal/BranchDetailsModal";
import { Alert, Snackbar } from "@mui/material";

export default function ManageBranches() {
    const { token, institution } = useAuth();
    const institutionId = institution?.id;

    const [isLoading, setIsLoading] = useState(false);
    const [branches, setBranches] = useState([]);
    const [editId, setEditId] = useState(null);
    const [selectedBranch, setSelectedBranch] = useState(null);
    const [branchModalOpen, setBranchModalOpen] = useState(false);

    const [confirmationModalOpen, setConfirmationModalOpen] = useState(false);
    const [branchToDelete, setBranchToDelete] = useState(null);

    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarContent, setSnackbarContent] = useState({
        message: "",
        type: "",
    });

    const onSave = async (id, newData) => {
        const isNew = id.toString().startsWith("temp-");

        try {
            if (isNew) {
                const newBranchData = {
                    name: newData.name,
                    city: newData.city,
                    street: newData.street,
                    description: newData.description || "",
                    workStartTime: "08:00",
                    workEndTime: "17:00",
                    institutionId,
                    isActive: newData.status === "فعال",
                };

                const res = await addBranch(token, newBranchData);

                const added = {
                    ...res.data,
                    status: newBranchData.isActive ? "فعال" : "غير فعال",
                    description: res.data.description || "",
                };

                setBranches((prev) => prev.map((b) => (b.id === id ? added : b)));

                setSnackbarContent({
                    message: "تم إضافة الفرع بنجاح",
                    type: "success",
                });
            } else {
                const updatedData = {
                    ...newData,
                    isActive: newData.status === "فعال",
                };
                delete updatedData.status;

                await editBranch(token, id, updatedData);

                setBranches((prev) =>
                    prev.map((b) => (b.id === id ? { ...b, ...newData } : b))
                );

                setSnackbarContent({
                    message: "تم تعديل معلومات الفرع بنجاح",
                    type: "success",
                });
            }

            setSnackbarOpen(true);
        } catch (err) {
            console.error(err);

            if (isNew) {
                setBranches((prev) => prev.filter((b) => b.id !== id));
            }

            setSnackbarContent({
                message: "حدث خطأ أثناء حفظ البيانات",
                type: "error",
            });
            setSnackbarOpen(true);
        } finally {
            setEditId(null);
            setBranchModalOpen(false);
        }
    };

    const onDelete = async () => {
        setIsLoading(true);
        setConfirmationModalOpen(false);
        try {
            if (branchToDelete) {
                await deleteBranch(token, branchToDelete);
                setBranches((prev) => prev.filter((b) => b.id !== branchToDelete));
                setSnackbarContent({ message: "تم حذف الفرع بنجاح", type: "success" });
                setSnackbarOpen(true);
            }
        } catch (err) {
            setSnackbarContent({
                message: err.response?.data || err.message,
                type: "error",
            });
            setSnackbarOpen(true);
        } finally {
            setIsLoading(false);
        }
    };

    const handleAdd = (setRecentlyAddedId) => {
        if (editId) return;

        const newId = "temp-" + Date.now();
        const newBranch = {
            id: newId,
            name: "",
            city: "",
            street: "",
            description: "",
            status: "غير فعال",
        };

        setBranches((prev) => [newBranch, ...prev]);
        setEditId(newId);
        setRecentlyAddedId(newId);
    };

    const handleEdit = (branch) => {
        setSelectedBranch(branch);
        setBranchModalOpen(true);
    };

    const handleCancelEdit = (canceledId) => {
        if (canceledId.toString().startsWith("temp-")) {
            setBranches((prev) => prev.filter((b) => b.id !== canceledId));
        }
        setEditId(null);
    };

    useEffect(() => {
        const getBranchesWithWorkingDays = async () => {
            setIsLoading(true);
            try {
                const res = await getInstitutionBranches(institutionId);
                const branches = res.data;

                const enriched = await Promise.all(
                    branches.map(async (b) => {
                        try {
                            const workingDaysRes = await getBranchWorkingDays(token, b.id);
                            return {
                                ...b,
                                status: b.isActive ? "فعال" : "غير فعال",
                                workingDays: workingDaysRes.data,
                            };
                        } catch (err) {
                            console.warn(`Failed to fetch working days for branch ${b.id}`, err);
                            return {
                                ...b,
                                status: b.isActive ? "فعال" : "غير فعال",
                                workingDays: [],
                            };
                        }
                    })
                );

                setBranches(enriched);
            } catch (e) {
                console.error(e);
            } finally {
                setIsLoading(false);
            }
        };

        if (institutionId) getBranchesWithWorkingDays();
    }, [institutionId]);

    const columns = [
        { key: "name", label: "اسم الفرع" },
        { key: "city", label: "المدينة" },
        { key: "street", label: "الشارع" },
        {
            key: "status",
            label: "الحالة",
            isDropdown: true,
            options: [
                { value: "فعال", label: "فعال" },
                { value: "غير فعال", label: "غير فعال" },
            ],
        },
        { key: "description", label: "الوصف" },
    ];

    return (
        <div className="page">
            <div className="content-view">
                <h1 className="text-xl font-bold mb-4">إدارة الفروع</h1>

                <Table
                    detailsColumnLabel="مواعيد الدوام"
                    data={branches}
                    columns={columns}
                    loading={isLoading}
                    onDelete={(row) => {
                        console.log("Selected branch deletion: ", row);
                        setBranchToDelete(row);
                        setConfirmationModalOpen(true);
                    }}
                    onSave={onSave}
                    onAdd={handleAdd}
                    onEdit={handleEdit}
                    onRowClick={handleEdit}
                    setEditId={setEditId}
                    editId={editId}
                    onCancelEdit={handleCancelEdit}
                    editableColumns={["name", "city", "street", "description", "status"]}
                    highlightConfig={{
                        status: {
                            "فعال": "#01c8aa",
                            "غير فعال": "#ef4444",
                        },
                    }}
                    tagColumn="status"
                />

                {branchModalOpen && selectedBranch?.id && (
                    <BranchDetailsModal
                        branchId={selectedBranch.id}
                        onClose={() => setBranchModalOpen(false)}
                    />
                )}

                {confirmationModalOpen && (
                    <ConfirmationModal
                        message="هل أنت متأكد أنك تريد حذف هذا الفرع؟"
                        handleConfirm={onDelete}
                        onClose={() => setConfirmationModalOpen(false)}
                    />
                )}

                <Snackbar
                    open={snackbarOpen}
                    autoHideDuration={6000}
                    onClose={() => setSnackbarOpen(false)}
                >
                    <Alert
                        onClose={() => setSnackbarOpen(false)}
                        severity={snackbarContent.type}
                        variant="filled"
                        sx={{ width: "100%" }}
                    >
                        {snackbarContent.message}
                    </Alert>
                </Snackbar>
            </div>
        </div>
    );
}
